import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  useMediaQuery,
  Autocomplete,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { fetchItemsByOrgId, fetchSoldItemById } from "../services/api";
import { useUser } from "../context/UserContext";
import LoadingIndicator from "../utility/LoadingIndicator"

const Reports = () => {
  const { user } = useUser();
  const [itemsByUser, setItemsByUser] = useState([]);
  const [soldItems, setSoldItems] = useState([]);
  const [aggregateSoldStock, setAggregateSoldStock] = useState([]);
  const [totalStockQuantitiesOfItem, setTotalStockQuantitiesOfItem] = useState(
    []
  );
  const [selectedItemDetails, setSelectedItemDetails] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  const [selectedItemInventoryDetails, setSelectedItemInventoryDetails] =
    useState(null);
  const [brand, setBrand] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLargeDevice = useMediaQuery(theme.breakpoints.up("lg"));
  const isExtraLargeDevice = useMediaQuery(theme.breakpoints.up("xl"));

  const styles = {
    paper: {
      padding: "10px",
      backgroundColor: "white",
      borderRadius: "8px",
      textAlign: "center",
    },
    title: {
      marginBottom: "16px",
      fontWeight: 600,
    },
    detailItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "8px",
    },
    label: {
      fontWeight: 500,
      color: "#555",
      flex: 1,
      textAlign: "right",
      paddingRight: "8px",
    },
    value: {
      color: "#333",
      fontWeight: 500,
      flex: 2,
      textAlign: "left",
    },
  };

  // Fetch all items by orgId
  const fetchItemsByUser = async () => {
    if (!user || !user.orgId) return;
    try {
      const response = await fetchItemsByOrgId(user.orgId);
      if (response && response.status === 200) {
        setItemsByUser(response.data.data);
        console.log("Items by orgId:", response.data.data);
      } else {
        setItemsByUser([]);
      }
    } catch (error) {
      console.error("Failed to fetch items:", error);
      setItemsByUser([]);
    }
  };

  // Calculate inventory data
  const calculateInventory = (totalStockQuantities, soldItemsData) => {
    const inventory = totalStockQuantities.map((item) => {
      const soldItem =
        soldItemsData.find((sold) => sold.itemName === item.itemName) || {};
      const soldQuantity = soldItem.soldCount || 0;
      const remainingStock =
        (item.item_stockQuantity || 0) - (soldItem.soldCount || 0);
      const purchasedCost =
        (item.item_stockQuantity || 0) * (item.item_cost || 0);
      const totalSellingCost =
        (item.item_stockQuantity || 0) * (item.item_sellingCost || 0);
      const remainingStockCost =
        ((item.item_stockQuantity || 0) - (soldItem.soldCount || 0)) * (item.item_cost || 0);
      const revenueFromSale = soldQuantity * (item.item_sellingCost || 0);

      return {
        itemId: item.itemId,
        itemName: item.itemName,
        totalStockQuantity: item.item_stockQuantity,
        soldQuantity,
        remainingStock,
        purchasedCost,
        totalSellingCost,
        remainingStockCost,
        revenueFromSale,
        itemCost: item.item_cost,
        itemSellingCost: item.item_sellingCost,
      };
    });

    console.log("Calculated Inventory:", inventory);
    return inventory;
  };

  // Fetch sold items and aggregate sold stock data
  const fetchSoldItemsByUser = async () => {
    if (!user || !user.orgId) return;
    try {
      const response = await fetchSoldItemById(user.orgId);
      if (
        response &&
        response.status === 200 &&
        Array.isArray(response.data.data)
      ) {
        const soldItemsData = response.data.data;
        setSoldItems(soldItemsData);

        // Aggregate sold items
        const aggregatedData = soldItemsData.reduce((acc, item) => {
          const existingItem = acc.find((i) => i.itemName === item.itemName);
          if (existingItem) {
            existingItem.soldCount += item.sold_stockQuantity;
          } else {
            acc.push({
              itemName: item.itemName,
              soldCount: item.sold_stockQuantity,
            });
          }
          return acc;
        }, []);

        setAggregateSoldStock(aggregatedData);
        console.log("Aggregated Sold Items:", aggregatedData);
      } else {
        setSoldItems([]);
      }
    } catch (error) {
      console.error("Failed to fetch sold items:", error);
    }
  };


  const aggregateStockQuantities = (data) => {
    const stockQuantities = data
      .slice() // Create a shallow copy of the array to avoid mutating the original
      .reverse() // Reverse the array to start aggregation from the last index
      .reduce((acc, item) => {
        const existingItem = acc.find((i) => i.itemName === item.itemName);
        if (existingItem) {
          existingItem.item_stockQuantity += item.item_stockQuantity;
        } else {
          acc.push({ ...item });
        }
        return acc;
      }, [])
      .reverse(); // Reverse again to restore the original order after aggregation

    setTotalStockQuantitiesOfItem(stockQuantities);
    console.log("Total Stock Quantities (from last index):", stockQuantities);
  };



  // Handle item selection
  const handleItemChange = (selectedItem) => {
    if (selectedItem) {
      setBrand(selectedItem.itemName);
      setSelectedItemDetails(selectedItem);
      const matchingInventory = inventoryData.find(
        (item) => item.itemName === selectedItem.itemName
      );
      setSelectedItemInventoryDetails(matchingInventory);
    } else {
      setBrand(null);
      setSelectedItemDetails(null);
    }
  };

  useEffect(() => {
    fetchItemsByUser();
    fetchSoldItemsByUser();
  }, []);

  useEffect(() => {
    if (itemsByUser.length > 0) {
      aggregateStockQuantities(itemsByUser);
    }
  }, [itemsByUser]);

  useEffect(() => {
    if (totalStockQuantitiesOfItem.length > 0 && soldItems.length >= 0) {
      const inventory = calculateInventory(
        totalStockQuantitiesOfItem,
        aggregateSoldStock
      );
      setInventoryData(inventory);
    }
  }, [totalStockQuantitiesOfItem, soldItems]);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (inventoryData.length === 0) return;

    // Set up an interval to change the index every 1 second
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % inventoryData.length);
    }, 1500);

    // Clear interval on component unmount
    return () => clearInterval(interval);
  }, [inventoryData]);


  return (
    <Grid container spacing={2}
      sx={{
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', // Soft shadow
        borderRadius: '8px', // Rounded corners for better aesthetics
        paddingBottom: "5px",
        backgroundColor: '#fff', // Ensure a background color for visibility
        minHeight: "82vh",
        
      }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between", // Ensures items align correctly
          width: "100%", // Takes full width of the parent container
          padding: "5 20px", // Optional padding for spacing from edges
          height:"18px"
        }}
      >
        {/* Left Section */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              color: "#024CAA",
              fontSize: "30px",
              margin:"20px"
            }}
          >
            Global Item Details
          </Typography>
        </Box>

        {/* Right Section */}
        <Box sx={{margin:"20px"}} >
          <Autocomplete
            options={totalStockQuantitiesOfItem}
            getOptionLabel={(item) => item.itemName}
            value={
              brand
                ? totalStockQuantitiesOfItem.find(
                  (item) => item.itemName === brand
                )
                : null
            }
            onChange={(e, selectedItem) => handleItemChange(selectedItem)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Items"
                placeholder="Select an Item"
                fullWidth
                size="small"
                sx={{
                  width: "300px",
                  "& .MuiInputBase-input": {
                    color: "#071952",
                  },
                  "& .MuiInputLabel-root": {
                    color: "#071952",
                  },
                  input: { color: "#071952", padding: "8px" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#102C57" },
                    "&:hover fieldset": { borderColor: "#071952" },
                    "&.Mui-focused fieldset": { borderColor: "#3572EF" },
                    
                  },
                }}
              />
            )}
          />
        </Box>
      </Box>



      {selectedItemDetails ? (
        <Grid item xs={12}>
        <Box sx={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
          {/* Header and Details Section */}
          <Box sx={{ borderRadius: "10px", overflow: "hidden", marginRight: "20px" }}>
            <Typography
              variant="h6"
              sx={{
                color: "white",
                background: "#133E87",
                padding: "10px",
                marginBottom: "0px",
                textAlign:"center"
              }}
            >
              Selected Item Details
            </Typography>
            <Paper
              elevation={3}
              style={{
                ...styles.paper,
                borderRadius: "0px 0px 20px 20px",
                border:"#F5F7F8  3px solid"
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                  margin: "0px 20px 20px 20px",
                  
                }}
              >
                {[
                  { label: "Item Name: ", value: selectedItemDetails.itemName },
                  { label: "Item ID", value: selectedItemDetails.itemId },
                  { label: "Issue Price", value: selectedItemInventoryDetails?.itemCost || "0" },
                  { label: "MRP", value: selectedItemInventoryDetails?.itemSellingCost || "0" },
                  { label: "Purchased Stock", value: selectedItemInventoryDetails?.totalStockQuantity || "0" },
                  { label: "Sold Stock", value: selectedItemInventoryDetails?.soldQuantity || "0" },
                  { label: "Remaining Stock", value: selectedItemInventoryDetails?.remainingStock || "0" },
                  { label: "Purchased Stock Price", value: selectedItemInventoryDetails?.purchasedCost || "0" },
                  { label: "Sold Stock Price ", value: selectedItemInventoryDetails?.revenueFromSale || "0" },
                  { label: "Remaining Stock Price", value: selectedItemInventoryDetails?.remainingStockCost || "0" },
                ].map((detail, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <Typography sx={{ color: "#270082", fontWeight: "bold" }}>
                      {detail.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "500",
                        color: "#024CAA",
                        textAlign: "right",
                      }}
                    >
                      {detail.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
      
          {/* Purchased, Sold, and Remaining Stock Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              marginTop: "10px",
              paddingTop: "10px",
            }}
          >
            <Paper elevation={3}  sx={{ borderRadius: "8px",
      textAlign: "center",padding:"10px"}}>
              <Box sx={{ minWidth: "200px" }}>
                <Typography sx={{ color: "#1640D6", fontSize: "24px" }}>
                  Purchased Stock:
                </Typography>
                <Typography sx={{ color: "#4335A7", fontSize: "20px" }}>
                  {selectedItemInventoryDetails.totalStockQuantity}
                </Typography>
              </Box>
            </Paper>
            <Paper elevation={3}   sx={{ borderRadius: "8px",
      textAlign: "center",padding:"10px"}}>
              <Box sx={{ minWidth: "200px" }}>
                <Typography sx={{ color: "#1640D6", fontSize: "24px" }}>
                  Sold Stock:
                </Typography>
                <Typography sx={{ color: "#4335A7", fontSize: "20px" }}>
                  {selectedItemInventoryDetails.soldQuantity}
                </Typography>
              </Box>
            </Paper>
            <Paper elevation={3} sx={{borderRadius: "8px",
      textAlign: "center",padding:"10px"}}>
              <Box sx={{ minWidth: "200px" }}>
                <Typography sx={{ color: "#1640D6", fontSize: "24px" }}>
                  Remaining Stock:
                </Typography>
                <Typography sx={{ color: "#4335A7", fontSize: "20px" }}>
                  {selectedItemInventoryDetails.remainingStock}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Grid>
      
      ) : (<div style={{ display: "flex", justifyContent: "center", marginTop: "0px", width: "100%" }}>

        {inventoryData.length > 0 ? (
          <div
            style={{
              border: "1px solid #ccc",
              borderRadius: "10px",
              padding: "20px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              width: "1000px",
              textAlign: "center",
              backgroundColor: "white",
              marginBottom:"20px"

            }}
          >
            <h3 style={{ color: "#4C3BCF",marginTop:"60px" }}>{inventoryData[currentIndex].itemName}</h3>
            <Box sx={{padding:"10px 30px 10px 30px"}}>
            <Box sx={{display:"flex",marginLeft:"40px" }}>
              <Box sx={{width:"50%",textAlign:"center"}}>
              <p style={{ color: "#050C9C" }}><strong>Item ID</strong> </p>
              </Box>
              <Box sx={{width:"20%"}}><strong>:</strong></Box>
              <Box sx={{width:"50%",textAlign:"center"}}>
              {`${inventoryData[currentIndex].itemId}`}
              </Box>
            </Box>
            <Box sx={{display:"flex",marginLeft:"40px" }}>
              <Box sx={{width:"50%",textAlign:"center"}}>
              <p style={{ color: "#050C9C" }}><strong>Total Stock</strong> </p>
              </Box>
              <Box sx={{width:"20%"}}><strong>:</strong></Box>
              <Box sx={{width:"50%",textAlign:"center"}}>
              {`${inventoryData[currentIndex].totalStockQuantity}`}
              </Box>
            </Box>
            <Box sx={{display:"flex",marginLeft:"40px" }}>
              <Box sx={{width:"50%",textAlign:"center"}}>
              <p style={{ color: "#050C9C" }}><strong>Sold Quantity</strong> </p>
              </Box>
              <Box sx={{width:"20%"}} ><strong>:</strong></Box>
              <Box sx={{width:"50%",textAlign:"center"}}>
                {`${inventoryData[currentIndex].soldQuantity}`}
              </Box>
            </Box>
            <Box sx={{display:"flex",marginLeft:"40px" }}>
              <Box sx={{width:"50%",textAlign:"center"}}>
              <p style={{ color: "#050C9C" }}><strong>Remaining Stock:</strong> </p>
              </Box>
              <Box sx={{width:"20%"}}><strong>:</strong></Box>
              <Box sx={{width:"50%",textAlign:"center"}}>
              {`${inventoryData[currentIndex].remainingStock}`}
              
              </Box>
            </Box>
            <Box sx={{display:"flex",marginLeft:"40px" }}>
              <Box sx={{width:"50%",textAlign:"center"}}>
              <p style={{ color: "#050C9C" }}><strong>Item Cost:</strong> </p>
              </Box>
              <Box sx={{width:"20%"}}><strong>:</strong></Box>
              <Box sx={{width:"50%",textAlign:"center"}}>
              {`₹${inventoryData[currentIndex].itemCost}`}

              
              </Box>
            </Box>
            <Box sx={{display:"flex",marginLeft:"40px" }}>
              <Box sx={{width:"50%",textAlign:"center"}}>
              <p style={{ color: "#050C9C" }}><strong>Selling Cost:</strong> </p>
              </Box>
              <Box sx={{width:"20%"}}><strong>:</strong></Box>
              <Box sx={{width:"50%",textAlign:"center"}}>
              {`₹${inventoryData[currentIndex].itemSellingCost}`}
              </Box>
            </Box>
            
            </Box>
            
          </div>
        ):(<LoadingIndicator/>)}
      </div>)}
    </Grid>
  );
};

export default Reports;
